import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'md-theme';

/** 실제 적용할 테마('light'|'dark')를 계산 */
function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return theme;
}

/** HTML 루트에 data-theme attribute + .dark 클래스를 동기 적용 */
function applyTheme(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  const isDark = resolved === 'dark';
  /* data-theme attribute — CSS 변수 토큰 기반 전환 */
  root.setAttribute('data-theme', resolved);
  /* .dark 클래스 — 기존 Tailwind dark: 유틸 호환 유지 */
  root.classList.toggle('dark', isDark);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(STORAGE_KEY) as Theme) || 'system';
  });

  useEffect(() => {
    const resolved = resolveTheme(theme);
    applyTheme(resolved);

    /* system 모드: 시스템 설정 변경 시 자동 업데이트 */
    if (theme === 'system') {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) =>
        applyTheme(e.matches ? 'dark' : 'light');
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }
  }, [theme]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
  };

  return { theme, setTheme };
}
